import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  BarChart3, TrendingUp, TrendingDown, Loader2, FileSpreadsheet, FileText,
  IndianRupee, Package, Clock, AlertTriangle, Layers, Wrench, CircleCheck,
  Building2, Filter, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { downloadBlob } from "@/components/DataTable";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as ReTooltip,
  CartesianGrid, BarChart, Bar, Legend, ComposedChart,
} from "recharts";

const RED = "#7f1d1d";
const NAVY = "#0B1F3A";
const GREEN = "#166534";
const AMBER = "#b45309";

function inr(n) {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}
function pct(n) { return n === null || n === undefined ? "—" : `${n > 0 ? "▲" : n < 0 ? "▼" : ""} ${Math.abs(n)}%`; }
function currentFyYear() { const d = new Date(); return d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1; }
function fyLabel(y) { return `${y}-${String(y + 1).slice(-2)}`; }

const ORDER_TYPES = ["", "New Manufacturing", "Reconditioning - PO First", "Reconditioning - Cylinder First"];

export default function Reports() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [fy, setFy] = useState(currentFyYear());
  const [customerId, setCustomerId] = useState("");
  const [orderType, setOrderType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [customers, setCustomers] = useState([]);
  const [tab, setTab] = useState("executive");

  useEffect(() => {
    api.get("/customers?limit=500").then(({ data }) => setCustomers(data.items || data || [])).catch(() => {});
  }, []);

  const filterQS = useMemo(() => {
    const q = new URLSearchParams({ fy: String(fy) });
    if (customerId) q.set("customer_id", customerId);
    if (orderType) q.set("order_type", orderType);
    if (dateFrom) q.set("date_from", dateFrom);
    if (dateTo) q.set("date_to", dateTo);
    return q.toString();
  }, [fy, customerId, orderType, dateFrom, dateTo]);

  function reset() {
    setFy(currentFyYear()); setCustomerId(""); setOrderType("");
    setDateFrom(""); setDateTo("");
  }

  async function exportReport(kind, format = "xlsx") {
    const token = localStorage.getItem("hispl_token");
    const res = await fetch(`${API}/reports/export/${kind}?${filterQS}&format=${format}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) { toast.error("Export failed — you may not have permission."); return; }
    await downloadBlob(res, `HISPL_${kind}_${fyLabel(fy)}.${format}`);
    toast.success(`${format.toUpperCase()} downloaded`);
  }

  const fyOptions = useMemo(() => {
    const c = currentFyYear();
    return [c + 1, c, c - 1, c - 2, c - 3];
  }, []);

  const TAB_EXPORTS = {
    executive: "executive",
    marketing: "marketing",
    "marketing-quarterly": "marketing-quarterly",
    "marketing-monthly": "marketing-monthly",
    production: "production",
    "design-performance": "design-performance",
    "production-quarterly": "production-quarterly",
    "order-execution": "order-execution",
  };

  return (
    <div className="max-w-[1500px] mx-auto space-y-5" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2">
            <BarChart3 className="w-3.5 h-3.5" style={{ color: RED }} />
            Management Analytics
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1">Reports</h1>
          <p className="text-sm text-slate-500 mt-1">
            Live KPIs computed on demand from HISPL NEXUS operational data. Every card, chart and row drills down to the underlying records.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => exportReport(TAB_EXPORTS[tab], "xlsx")} data-testid="btn-export-xlsx">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportReport(TAB_EXPORTS[tab], "pdf")} data-testid="btn-export-pdf">
            <FileText className="w-4 h-4 mr-2" /> PDF
          </Button>
        </div>
      </div>

      {/* Global Filter Bar */}
      <Card className="p-3" data-testid="filter-bar">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-semibold">
            <Filter className="w-3.5 h-3.5" /> Filters
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500 mb-1">FY</div>
            <Select value={String(fy)} onValueChange={(v) => setFy(Number(v))}>
              <SelectTrigger className="w-28 h-9" data-testid="fy-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {fyOptions.map((y) => (<SelectItem key={y} value={String(y)}>{fyLabel(y)}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500 mb-1">Customer</div>
            <Select value={customerId || "all"} onValueChange={(v) => setCustomerId(v === "all" ? "" : v)}>
              <SelectTrigger className="w-56 h-9" data-testid="customer-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All customers</SelectItem>
                {customers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500 mb-1">Order Type</div>
            <Select value={orderType || "all"} onValueChange={(v) => setOrderType(v === "all" ? "" : v)}>
              <SelectTrigger className="w-48 h-9" data-testid="order-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {ORDER_TYPES.filter(Boolean).map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500 mb-1">From</div>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36 h-9" data-testid="date-from-filter" />
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500 mb-1">To</div>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36 h-9" data-testid="date-to-filter" />
          </div>
          <Button variant="ghost" size="sm" onClick={reset} data-testid="btn-reset-filters">
            <RotateCcw className="w-4 h-4 mr-1" /> Reset
          </Button>
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="executive" data-testid="tab-executive">Executive Summary</TabsTrigger>
          <TabsTrigger value="marketing" data-testid="tab-marketing">Marketing</TabsTrigger>
          <TabsTrigger value="marketing-quarterly" data-testid="tab-mkt-quarterly">Marketing · Quarterly</TabsTrigger>
          <TabsTrigger value="marketing-monthly" data-testid="tab-mkt-monthly">Marketing · Monthly Trend</TabsTrigger>
          <TabsTrigger value="production" data-testid="tab-production">Production</TabsTrigger>
          <TabsTrigger value="design-performance" data-testid="tab-design">Design Performance</TabsTrigger>
          <TabsTrigger value="production-quarterly" data-testid="tab-prod-quarterly">Production · Quarterly</TabsTrigger>
          <TabsTrigger value="order-execution" data-testid="tab-order-exec">Order Execution</TabsTrigger>
        </TabsList>

        <TabsContent value="executive"><ExecutiveTab qs={filterQS} setTab={setTab} nav={nav} /></TabsContent>
        <TabsContent value="marketing"><MarketingTab qs={filterQS} nav={nav} /></TabsContent>
        <TabsContent value="marketing-quarterly"><MarketingQuarterlyTab qs={filterQS} /></TabsContent>
        <TabsContent value="marketing-monthly"><MarketingMonthlyTab qs={filterQS} /></TabsContent>
        <TabsContent value="production"><ProductionTab qs={filterQS} nav={nav} customerId={customerId} /></TabsContent>
        <TabsContent value="design-performance"><DesignPerformanceTab qs={filterQS} nav={nav} /></TabsContent>
        <TabsContent value="production-quarterly"><ProductionQuarterlyTab qs={filterQS} /></TabsContent>
        <TabsContent value="order-execution"><OrderExecutionTab qs={filterQS} nav={nav} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Shared UI ---------------- */
function KpiCard({ label, value, sub, accent, icon: Icon, onClick, testid }) {
  const cls = accent === "red" ? "border-red-200 bg-red-50/40"
    : accent === "amber" ? "border-amber-200 bg-amber-50/40"
    : accent === "green" ? "border-emerald-200 bg-emerald-50/40"
    : accent === "navy" ? "border-slate-300 bg-slate-50"
    : "border-slate-200 bg-white";
  return (
    <Card className={`p-3 ${onClick ? "cursor-pointer hover:shadow-md transition" : ""} ${cls}`} onClick={onClick} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="text-xl font-bold text-slate-900 mt-1">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </Card>
  );
}

function RowsTable({ rows, cols, onRow, testidPrefix, empty = "No records." }) {
  if (!rows || rows.length === 0) return <div className="text-sm text-slate-500 italic py-4">{empty}</div>;
  return (
    <div className="border rounded overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr>{cols.map((c) => (
            <th key={c.k} className={`px-3 py-2 ${c.align === "right" ? "text-right" : "text-left"} font-semibold`}>{c.h}</th>
          ))}</tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <tr key={i} className={`${onRow ? "hover:bg-red-50/30 cursor-pointer" : ""}`}
                onClick={() => onRow && onRow(r)}
                data-testid={`${testidPrefix}-${i}`}>
              {cols.map((c) => (
                <td key={c.k} className={`px-3 py-2 ${c.align === "right" ? "text-right" : ""}`}>
                  {c.render ? c.render(r[c.k], r) : (r[c.k] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Loading() { return <div className="flex items-center gap-2 text-sm text-slate-500 py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>; }

/* ---------------- Executive Summary ---------------- */
function ExecutiveTab({ qs, setTab, nav }) {
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get(`/reports/executive-summary?${qs}`).then(({ data }) => setData(data)).catch(() => setData({ error: true })); }, [qs]);
  if (!data) return <Loading />;
  if (data.error) return <Card className="p-6 text-sm text-slate-500">Failed to load report.</Card>;
  const k = data.kpis;
  return (
    <div className="space-y-5" data-testid="executive-summary">
      <Card className="p-5">
        <div className="text-xs uppercase text-slate-500 font-semibold mb-3">Financial Year {data.fy_label} · vs {data.prev_fy_label}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="FY Order Booked" value={inr(k.fy_order_booked)} sub={`YoY ${pct(k.fy_order_booked_yoy_pct)}`}
            accent={k.fy_order_booked_yoy_pct > 0 ? "green" : "amber"} icon={IndianRupee} onClick={() => setTab("marketing")} testid="k-fy-booked" />
          <KpiCard label="FY Executed" value={inr(k.fy_executed)} sub={`YoY ${pct(k.executed_yoy_pct)}`}
            accent="navy" icon={CircleCheck} onClick={() => setTab("order-execution")} testid="k-fy-executed" />
          <KpiCard label="Pending Order Value" value={inr(k.pending_order_value)} sub={`${k.execution_pct}% executed`}
            accent="amber" icon={Clock} onClick={() => setTab("order-execution")} testid="k-pending" />
          <KpiCard label="Enq → Qtn %" value={`${k.conversion_pct}%`} sub={`${k.quoted_fy}/${k.enquiries_fy} enquiries`}
            accent="red" icon={TrendingUp} onClick={() => setTab("marketing")} testid="k-conv" />
        </div>
      </Card>

      <Card className="p-5">
        <div className="text-xs uppercase text-slate-500 font-semibold mb-3">Month-to-Date</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="MTD Booked" value={inr(k.mtd_order_booked)} sub={`Prev month ${inr(k.prev_month_order_booked)}`} testid="k-mtd" />
          <KpiCard label="MoM %" value={pct(k.mom_pct)} accent={k.mom_pct > 0 ? "green" : "amber"} testid="k-mom" />
          <KpiCard label="MTD Executed" value={inr(k.mtd_executed)} testid="k-mtd-exec" />
          <KpiCard label="Execution %" value={`${k.execution_pct}%`} accent="green" testid="k-exec-pct" />
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Card className="p-5">
          <div className="text-xs uppercase text-slate-500 font-semibold mb-3 flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> Sales Pipeline</div>
          <div className="grid grid-cols-2 gap-3">
            <KpiCard label="Open Enquiries" value={data.pipeline.open_inquiries} testid="k-open-inq"
                    onClick={() => nav("/inquiries?status=Open")} />
            <KpiCard label="Open Quotations" value={data.pipeline.open_quotations} testid="k-open-qtn"
                    onClick={() => nav("/quotations?status=Sent")} />
            <KpiCard label="Pending POs" value={data.pipeline.pending_pos} testid="k-pending-po"
                    onClick={() => nav("/purchase-orders?status=Received")} />
            <KpiCard label="Awaiting OA" value={data.pipeline.awaiting_oa} testid="k-awaiting-oa"
                    onClick={() => nav("/purchase-orders?status=Accepted")} />
          </div>
        </Card>
        <Card className="p-5">
          <div className="text-xs uppercase text-slate-500 font-semibold mb-3 flex items-center gap-2"><Package className="w-3.5 h-3.5" /> Shop Floor</div>
          <div className="grid grid-cols-3 gap-3">
            <KpiCard label="Active" value={data.shop_floor.active_cylinders} icon={Layers} testid="k-active"
                    onClick={() => nav("/lifecycle?status=Active")} />
            <KpiCard label="Ready for Dispatch" value={data.shop_floor.ready_for_dispatch} accent="green" icon={CircleCheck} testid="k-ready"
                    onClick={() => nav("/lifecycle?status=Ready")} />
            <KpiCard label="Delayed" value={data.shop_floor.delayed} accent="red" icon={AlertTriangle} testid="k-delayed"
                    onClick={() => nav("/lifecycle?delayed=1")} />
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ---------------- Marketing ---------------- */
function MarketingTab({ qs, nav }) {
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get(`/reports/marketing?${qs}`).then(({ data }) => setData(data)).catch(() => setData({ error: true })); }, [qs]);
  if (!data) return <Loading />;
  if (data.error) return <Card className="p-6 text-sm text-slate-500">Failed to load report.</Card>;
  const ob = data.order_booked, cv = data.conversion, ce = data.customer_execution;
  return (
    <div className="space-y-5">
      {/* Order Booked */}
      <Card className="p-5" data-testid="section-order-booked">
        <h3 className="font-semibold text-slate-900 mb-3">1. Order Booked <span className="text-[10px] uppercase text-slate-500 font-normal ml-2">source: customer POs received</span></h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <KpiCard label="FY Total" value={inr(ob.fy_total)} sub={`${ob.fy_count} POs`} accent="red" testid="ob-fy" />
          <KpiCard label="Current Month" value={inr(ob.current_month_total)} sub={`${ob.current_month_count} POs`} testid="ob-cm" />
          <KpiCard label="Previous Month" value={inr(ob.previous_month_total)} testid="ob-pm" />
          <KpiCard label="MoM" value={pct(ob.mom_pct)} accent={ob.mom_pct > 0 ? "green" : "amber"} testid="ob-mom" />
        </div>
        <RowsTable rows={ob.rows.slice(0, 30)} cols={[
          { k: "po_number", h: "PO Number" }, { k: "po_date", h: "PO Date" },
          { k: "customer_name", h: "Customer" }, { k: "quotation_number", h: "Quotation" },
          { k: "order_value", h: "Value", align: "right", render: inr },
          { k: "status", h: "Status" },
        ]} onRow={(r) => nav(`/purchase-orders/${r.id}`)} testidPrefix="ob-row" />
      </Card>

      {/* Quotation → Order Conversion */}
      {data.quotation_conversion && (
        <Card className="p-5" data-testid="section-qtn-order-conversion">
          <h3 className="font-semibold text-slate-900 mb-3">1a. Quotation → Order Conversion</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Quotations Sent" value={data.quotation_conversion.total} testid="qoc-total" />
            <KpiCard label="Converted (PO Received)" value={data.quotation_conversion.converted} accent="green" testid="qoc-conv" />
            <KpiCard label="Pending" value={data.quotation_conversion.pending} accent="amber" testid="qoc-pending" />
            <KpiCard label="Conversion %" value={`${data.quotation_conversion.pct}%`} accent="red" testid="qoc-pct" />
          </div>
        </Card>
      )}

      {/* Conversion */}
      <Card className="p-5" data-testid="section-conversion">
        <h3 className="font-semibold text-slate-900 mb-3">2. Enquiry → Quotation Conversion</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <KpiCard label="Enquiries" value={cv.fy_stats.total} testid="cv-total" />
          <KpiCard label="Quoted" value={cv.fy_stats.quoted} accent="green" testid="cv-quoted" />
          <KpiCard label="Pending" value={cv.fy_stats.pending} accent="amber" testid="cv-pending" />
          <KpiCard label="Conversion %" value={`${cv.fy_stats.conversion_pct}%`} accent="red" testid="cv-pct" />
        </div>
        {cv.pending_rows?.length > 0 && (
          <>
            <div className="text-xs font-semibold text-slate-600 uppercase mb-2">Pending (unquoted) enquiries</div>
            <RowsTable rows={cv.pending_rows.slice(0, 15)} cols={[
              { k: "inquiry_number", h: "Number" }, { k: "inquiry_date", h: "Date" },
              { k: "customer_name", h: "Customer" }, { k: "subject", h: "Subject" },
              { k: "status", h: "Status" },
            ]} onRow={(r) => nav(`/inquiries/${r.id}`)} testidPrefix="cv-pending-row" />
          </>
        )}
      </Card>

      {/* Customer Execution */}
      <Card className="p-5" data-testid="section-customer-execution">
        <h3 className="font-semibold text-slate-900 mb-3">3. Customer-wise Order Booked vs Executed</h3>
        {ce.rows.length === 0 ? (
          <div className="text-sm text-slate-500 italic">No data for this filter.</div>
        ) : (
          <>
            <div className="h-64 mb-4">
              <ResponsiveContainer>
                <BarChart data={ce.rows.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="customer_name" fontSize={10} interval={0} angle={-20} textAnchor="end" height={70} />
                  <YAxis fontSize={11} />
                  <ReTooltip formatter={(v) => inr(v)} />
                  <Legend />
                  <Bar dataKey="booked_value" fill={RED} name="Booked" />
                  <Bar dataKey="executed_value" fill={GREEN} name="Executed" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <RowsTable rows={ce.rows} cols={[
              { k: "customer_name", h: "Customer" },
              { k: "oa_count", h: "OAs", align: "right" },
              { k: "booked_value", h: "Booked", align: "right", render: inr },
              { k: "executed_value", h: "Executed", align: "right", render: inr },
              { k: "pending_value", h: "Pending", align: "right", render: inr },
              { k: "execution_pct", h: "Exec %", align: "right", render: (v) => `${v}%` },
            ]} onRow={(r) => nav(`/cylinders?customer_id=${r.customer_id}`)} testidPrefix="ce-row" />
          </>
        )}
      </Card>
    </div>
  );
}

/* ---------------- Marketing Quarterly ---------------- */
function MarketingQuarterlyTab({ qs }) {
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get(`/reports/marketing-quarterly?${qs}`).then(({ data }) => setData(data)).catch(() => setData({ error: true })); }, [qs]);
  if (!data) return <Loading />;
  if (data.error) return <Card className="p-6 text-sm text-slate-500">Failed to load report.</Card>;
  const chartData = data.quarters.map((q) => ({
    quarter: q.quarter,
    [data.fy_label]: q.current_booked,
    [data.prev_fy_label]: q.prev_booked,
    yoy: q.booked_yoy_pct,
  }));
  return (
    <div className="space-y-5">
      <Card className="p-5" data-testid="section-mkt-quarterly">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-900">Quarterly Marketing — FY {data.fy_label} vs {data.prev_fy_label}</h3>
          <div className="text-sm text-slate-600">
            FY Total: <strong>{inr(data.fy_totals.booked)}</strong> · YoY {pct(data.fy_totals.booked_yoy_pct)}
          </div>
        </div>
        <div className="h-72 mb-4">
          <ResponsiveContainer>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="quarter" fontSize={12} />
              <YAxis fontSize={11} />
              <ReTooltip formatter={(v) => inr(v)} />
              <Legend />
              <Bar dataKey={data.fy_label} fill={RED} />
              <Bar dataKey={data.prev_fy_label} fill={NAVY} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <RowsTable rows={data.quarters} cols={[
          { k: "label", h: "Quarter" },
          { k: "current_booked", h: `Booked ${data.fy_label}`, align: "right", render: inr },
          { k: "prev_booked", h: `Booked ${data.prev_fy_label}`, align: "right", render: inr },
          { k: "booked_yoy_pct", h: "YoY %", align: "right", render: pct },
          { k: "current_executed", h: `Executed ${data.fy_label}`, align: "right", render: inr },
          { k: "prev_executed", h: `Executed ${data.prev_fy_label}`, align: "right", render: inr },
          { k: "current_enquiries", h: "Enquiries", align: "right" },
          { k: "current_conversion", h: "Conv %", align: "right", render: (v) => `${v}%` },
        ]} testidPrefix="mq-row" />
      </Card>
    </div>
  );
}

/* ---------------- Marketing Monthly Trend ---------------- */
function MarketingMonthlyTab({ qs }) {
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get(`/reports/marketing-monthly-trend?${qs}`).then(({ data }) => setData(data)).catch(() => setData({ error: true })); }, [qs]);
  if (!data) return <Loading />;
  if (data.error) return <Card className="p-6 text-sm text-slate-500">Failed to load report.</Card>;
  return (
    <div className="space-y-5">
      <Card className="p-5" data-testid="section-mkt-monthly">
        <h3 className="font-semibold text-slate-900 mb-3">Monthly Trend — FY {data.fy_label}</h3>
        <div className="h-72 mb-4">
          <ResponsiveContainer>
            <ComposedChart data={data.months}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" fontSize={12} />
              <YAxis yAxisId="left" fontSize={11} />
              <YAxis yAxisId="right" orientation="right" fontSize={11} />
              <ReTooltip formatter={(v, k) => k === "enquiries" || k === "quoted" ? v : inr(v)} />
              <Legend />
              <Bar yAxisId="left" dataKey="order_booked" fill={RED} name="Order Booked" />
              <Line yAxisId="left" dataKey="prev_year_booked" stroke={NAVY} name={`Booked ${data.prev_fy_label}`} strokeDasharray="4 4" />
              <Line yAxisId="left" dataKey="executed" stroke={GREEN} name="Executed" strokeWidth={2} />
              <Line yAxisId="right" dataKey="enquiries" stroke={AMBER} name="Enquiries" strokeWidth={2} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <RowsTable rows={data.months} cols={[
          { k: "label", h: "Month" },
          { k: "order_booked", h: `Booked ${data.fy_label}`, align: "right", render: inr },
          { k: "prev_year_booked", h: `Booked ${data.prev_fy_label}`, align: "right", render: inr },
          { k: "executed", h: "Executed", align: "right", render: inr },
          { k: "enquiries", h: "Enquiries", align: "right" },
          { k: "quoted", h: "Quoted", align: "right" },
          { k: "conversion_pct", h: "Conv %", align: "right", render: (v) => `${v}%` },
        ]} testidPrefix="mm-row" />
      </Card>
    </div>
  );
}

/* ---------------- Production ---------------- */
function ProductionTab({ qs, nav, customerId }) {
  const [data, setData] = useState(null);
  const [stage, setStage] = useState(null);
  useEffect(() => {
    setData(null);
    const q = new URLSearchParams(qs);
    if (stage) q.set("stage", stage);
    api.get(`/reports/production?${q.toString()}`).then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  }, [qs, stage]);
  if (!data) return <Loading />;
  if (data.error) return <Card className="p-6 text-sm text-slate-500">Failed to load report.</Card>;
  const sev = (s) => s === "Severely Delayed" ? "bg-red-100 text-red-800 border-red-200"
    : s === "Delayed" ? "bg-amber-100 text-amber-800 border-amber-200"
    : s === "Due Soon" ? "bg-yellow-50 text-yellow-800 border-yellow-200"
    : "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <div className="space-y-5">
      <Card className="p-5" data-testid="section-design-pending">
        <h3 className="font-semibold text-slate-900 mb-3">1. Cylinders Pending in Design ({data.design_pending.total})</h3>
        <RowsTable rows={data.design_pending.rows} cols={[
          { k: "cylinder_number", h: "Cylinder" }, { k: "customer_name", h: "Customer" },
          { k: "oa_number", h: "OA" }, { k: "oa_release_date", h: "OA Release" },
          { k: "due_date", h: "Due" }, { k: "design_status", h: "Status" },
          { k: "days_in_design", h: "Days", align: "right",
            render: (v, r) => <span className={r.overdue ? "text-red-700 font-semibold" : ""}>{v ?? "—"}</span> },
        ]} onRow={(r) => nav(`/cylinders/${r.id}`)} testidPrefix="dp-row" empty="No cylinders pending in design." />
      </Card>

      <Card className="p-5" data-testid="section-in-production">
        <h3 className="font-semibold text-slate-900 mb-3">2. In Production ({data.in_production.total}) — click a stage to drill down</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
          {data.in_production.breakdown.map((b) => (
            <Card key={b.key}
              className={`p-3 cursor-pointer transition ${stage === b.key ? "border-red-500 shadow" : "hover:shadow"}`}
              onClick={() => setStage(stage === b.key ? null : b.key)}
              data-testid={`stage-${b.key}`}>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{b.label}</div>
              <div className="text-xl font-bold text-slate-900 mt-1">{b.count}</div>
            </Card>
          ))}
        </div>
        {stage && (
          <RowsTable rows={data.in_production.rows} cols={[
            { k: "cylinder_number", h: "Cylinder" }, { k: "customer_name", h: "Customer" },
            { k: "cylinder_status", h: "Status" }, { k: "current_stage", h: "Stage" },
            { k: "planned_dispatch_date", h: "Planned Dispatch" },
          ]} onRow={(r) => nav(`/cylinders/${r.id}`)} testidPrefix="ip-row" empty="No cylinders at this stage." />
        )}
      </Card>

      <Card className="p-5" data-testid="section-delayed">
        <h3 className="font-semibold text-slate-900 mb-3">3. Delayed Cylinders ({data.delayed.total})</h3>
        <RowsTable rows={data.delayed.rows} cols={[
          { k: "cylinder_number", h: "Cylinder" }, { k: "customer_name", h: "Customer" },
          { k: "po_number", h: "PO" }, { k: "oa_number", h: "OA" },
          { k: "due_date", h: "Due" }, { k: "current_stage", h: "Stage" },
          { k: "days_delayed", h: "Days Late", align: "right",
            render: (v) => <span className="font-semibold text-red-700">{v}</span> },
          { k: "severity", h: "Severity",
            render: (v) => <span className={`inline-block text-[10px] font-bold uppercase tracking-wider border rounded px-1.5 py-0.5 ${sev(v)}`}>{v}</span> },
        ]} onRow={(r) => nav(`/cylinders/${r.id}`)} testidPrefix="dl-row" empty="No delayed cylinders." />
      </Card>
    </div>
  );
}

/* ---------------- Design Performance ---------------- */
function DesignPerformanceTab({ qs, nav }) {
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get(`/reports/design-performance?${qs}`).then(({ data }) => setData(data)).catch(() => setData({ error: true })); }, [qs]);
  if (!data) return <Loading />;
  if (data.error) return <Card className="p-6 text-sm text-slate-500">Failed to load report.</Card>;
  const k = data.kpis;
  return (
    <div className="space-y-5">
      <Card className="p-5" data-testid="section-design-perf">
        <h3 className="font-semibold text-slate-900 mb-3">Design Performance — FY {data.fy_label}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <KpiCard label="Total Designs" value={k.total_designs} testid="dp-total" />
          <KpiCard label="Released" value={k.released} accent="green" testid="dp-released" />
          <KpiCard label="Pending" value={k.pending} accent="amber" testid="dp-pending" />
          <KpiCard label="Rejected" value={k.rejected} accent="red" testid="dp-rejected" />
          <KpiCard label="Approval %" value={`${k.approval_pct}%`} testid="dp-approval-pct" />
          <KpiCard label="Rework %" value={`${k.rework_pct}%`} accent="amber" testid="dp-rework-pct" />
          <KpiCard label="Avg Lead Time" value={`${k.avg_lead_time_days} d`} accent="navy" testid="dp-lead-time" />
        </div>
      </Card>
      <Card className="p-5">
        <RowsTable rows={data.rows} cols={[
          { k: "cylinder_number", h: "Cylinder" }, { k: "customer_name", h: "Customer" },
          { k: "ga_drawing_number", h: "GA Drawing" }, { k: "designer", h: "Designer" },
          { k: "created_at", h: "Created" }, { k: "approved_at", h: "Approved" },
          { k: "lead_time_days", h: "Lead Time (d)", align: "right" },
          { k: "revision_count", h: "Rev", align: "right" },
        ]} onRow={(r) => nav(`/engineering/${r.id}`)} testidPrefix="dp-row" empty="No designs match the filter." />
      </Card>
    </div>
  );
}

/* ---------------- Production Quarterly ---------------- */
function ProductionQuarterlyTab({ qs }) {
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get(`/reports/production-quarterly?${qs}`).then(({ data }) => setData(data)).catch(() => setData({ error: true })); }, [qs]);
  if (!data) return <Loading />;
  if (data.error) return <Card className="p-6 text-sm text-slate-500">Failed to load report.</Card>;
  const chartData = data.quarters.map((q) => ({
    quarter: q.quarter,
    [data.fy_label]: q.current_dispatched_value,
    [data.prev_fy_label]: q.prev_dispatched_value,
  }));
  return (
    <Card className="p-5" data-testid="section-prod-quarterly">
      <h3 className="font-semibold text-slate-900 mb-3">Production Quarterly — Dispatched Value FY {data.fy_label} vs {data.prev_fy_label}</h3>
      <div className="h-72 mb-4">
        <ResponsiveContainer>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="quarter" fontSize={12} />
            <YAxis fontSize={11} />
            <ReTooltip formatter={(v) => inr(v)} />
            <Legend />
            <Bar dataKey={data.fy_label} fill={GREEN} />
            <Bar dataKey={data.prev_fy_label} fill={NAVY} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <RowsTable rows={data.quarters} cols={[
        { k: "label", h: "Quarter" },
        { k: "current_dispatched_value", h: `Dispatched ${data.fy_label}`, align: "right", render: inr },
        { k: "prev_dispatched_value", h: `Dispatched ${data.prev_fy_label}`, align: "right", render: inr },
        { k: "current_dispatched_count", h: "Cylinders", align: "right" },
        { k: "yoy_pct", h: "YoY %", align: "right", render: pct },
      ]} testidPrefix="pq-row" />
    </Card>
  );
}

/* ---------------- Order Execution ---------------- */
function OrderExecutionTab({ qs, nav }) {
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get(`/reports/order-execution?${qs}`).then(({ data }) => setData(data)).catch(() => setData({ error: true })); }, [qs]);
  if (!data) return <Loading />;
  if (data.error) return <Card className="p-6 text-sm text-slate-500">Failed to load report.</Card>;
  const t = data.totals;
  const statusColor = (s) => s === "Completed" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
    : s === "In Progress" ? "bg-blue-100 text-blue-800 border-blue-200"
    : "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <div className="space-y-5">
      <Card className="p-5" data-testid="section-order-exec-totals">
        <h3 className="font-semibold text-slate-900 mb-3">Order Execution — FY {data.fy_label}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <KpiCard label="Total Booked" value={inr(t.booked)} accent="red" testid="oe-booked" />
          <KpiCard label="Total Executed" value={inr(t.executed)} accent="green" testid="oe-executed" />
          <KpiCard label="Total Pending" value={inr(t.pending)} accent="amber" testid="oe-pending" />
          <KpiCard label="Execution %" value={`${t.execution_pct}%`} testid="oe-exec-pct" />
          <KpiCard label="Cylinders (Total)" value={t.cyl_total} icon={Layers} testid="oe-cyl-total" />
          <KpiCard label="Cylinders (Dispatched)" value={t.cyl_dispatched} icon={CircleCheck} accent="green" testid="oe-cyl-disp" />
        </div>
      </Card>
      <Card className="p-5">
        <RowsTable rows={data.rows} cols={[
          { k: "oa_number", h: "OA" }, { k: "oa_date", h: "Date" },
          { k: "customer_name", h: "Customer" }, { k: "po_number", h: "PO" },
          { k: "order_type", h: "Type" },
          { k: "total_cylinders", h: "Cyl", align: "right" },
          { k: "dispatched_cylinders", h: "Disp", align: "right" },
          { k: "booked_value", h: "Booked", align: "right", render: inr },
          { k: "executed_value", h: "Executed", align: "right", render: inr },
          { k: "pending_value", h: "Pending", align: "right", render: inr },
          { k: "execution_pct", h: "Exec %", align: "right", render: (v) => `${v}%` },
          { k: "status", h: "Status",
            render: (v) => <span className={`inline-block text-[10px] font-bold uppercase tracking-wider border rounded px-1.5 py-0.5 ${statusColor(v)}`}>{v}</span> },
        ]} onRow={(r) => nav(`/order-acceptances/${r.id}`)} testidPrefix="oe-row" empty="No orders match the filter." />
      </Card>
    </div>
  );
}
